import { ComponentProps, FC, useCallback, useEffect, useMemo } from 'react'
import { stencil } from 'react-common'

import { useTranslation } from 'react-i18next'

import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth/useAuth'
import { useAuthThunks } from '@/hooks/useAuthThunks'
import { useIsMobileNew } from '@/hooks/useIsMobileNew'
import { useLocale } from './useLocale'
import { UserMenuNarrow } from '../UserMenuNarrow/UserMenuNarrow'
import { UserMenuWide } from '../UserMenuWide/UserMenuWide'

const { LOCALE_LABELS } = stencil
export type Locales = keyof typeof LOCALE_LABELS

type SelectEvent = Parameters<ComponentProps<typeof UserMenuNarrow>['onSelect']>[0]

export const UserMenuPreset: FC = () => {
  const { i18n, t } = useTranslation()
  const { user } = useAuth()
  const { handleLogout } = useAuthThunks()
  const isMobile = useIsMobileNew()

  const { localeMenu, setLocale, currentLocale } = useLocale()

  const userName = `${user?.first_name} ${user?.last_name}`

  const menu = useMemo(() => {
    return [
      {
        label: `${t('_pageHeader.userMenu.language')}: ${LOCALE_LABELS[currentLocale]}`,
        key: 'language',
        items: localeMenu.items,
      },
      {
        label: t('_pageHeader.userMenu.profile'),
        key: 'profile',
        testId: 'profile',
      },
      {
        label: t('_pageHeader.userMenu.signOut'),
        key: 'logout',
        testId: 'logout',
      },
    ]
  }, [currentLocale, localeMenu.items, t])

  const navigate = useNavigate()

  const handleSelect = useCallback(
    (event: SelectEvent) => {
      const firstKey = event.keyPath[event.keyPath.length - 1]
      switch (firstKey) {
        case 'language': {
          setLocale(event.key as Locales)
          break
        }
        case 'profile': {
          navigate('/profile')
          break
        }
        case 'logout': {
          handleLogout()
          break
        }
      }
    },
    [handleLogout, setLocale, navigate],
  )

  useEffect(() => {
    i18n.changeLanguage(currentLocale)
  }, [currentLocale, i18n])

  if (isMobile) {
    return <UserMenuNarrow userName={userName} menu={menu} onSelect={handleSelect} currentLocale={currentLocale} />
  }

  return <UserMenuWide userName={userName} menu={menu} onSelect={handleSelect} currentLocale={currentLocale} />
}
